import { Check, Circle } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import {
  getSignatureCompletion,
  type SignatureCompletion,
  type WalaSignature,
} from './ics204SignatureSlots';

type Completion = SignatureCompletion;

type Ics204SignatureProgressRingProps = {
  completion?: Completion;
  signatures?: WalaSignature[] | null;
  className?: string;
  size?: number;
  testId?: string;
  /** Use a span when nested inside another button (e.g. 204A outline button). */
  triggerAs?: 'button' | 'span';
  /** Tooltip / aria title prefix. Tasks use "Task Lifecycle". */
  progressTooltipTitle?: string;
};

/** Hub-style circular signature progress; hidden for unsigned draft. */
const Ics204SignatureProgressRing = ({
  completion: completionProp,
  signatures,
  className,
  size = 22,
  testId = 'ics204-signature-progress-ring',
  triggerAs = 'button',
  progressTooltipTitle = 'Signature progress',
}: Ics204SignatureProgressRingProps) => {
  const completion = completionProp ?? getSignatureCompletion(signatures ?? []);

  if (completion.requiredTotal <= 1 || completion.status === 'draft') {
    return null;
  }

  const progressAria = `${progressTooltipTitle} ${completion.completedCount} of ${completion.requiredTotal}`;

  const stroke = 3;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (completion.percent / 100) * circumference;

  const triggerClassName = cn(
    'tw:inline-flex tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:text-emerald-200 tw:outline-none tw:focus-visible:ring-2 tw:focus-visible:ring-ring',
    className
  );

  const ringSvg = (
    <svg width={size} height={size} className="tw:-rotate-90">
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeWidth={stroke}
        className="tw:opacity-20"
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        className={
          completion.percent >= 100
            ? 'tw:text-emerald-400'
            : 'tw:text-emerald-300'
        }
      />
    </svg>
  );

  return (
    <TooltipProvider delay={200}>
      <Tooltip>
        <TooltipTrigger
          render={
            triggerAs === 'span' ? (
              <span
                className={triggerClassName}
                aria-label={progressAria}
                data-testid={testId}
              >
                {ringSvg}
              </span>
            ) : (
              <button
                type="button"
                className={triggerClassName}
                aria-label={progressAria}
                data-testid={testId}
                onClick={(event) => event.stopPropagation()}
              >
                {ringSvg}
              </button>
            )
          }
        />
        <TooltipContent side="top" className="tw:max-w-[16rem] tw:p-2">
          <p className="tw:mb-1.5 tw:text-[11px] tw:font-semibold">
            {progressTooltipTitle} · {completion.completedCount}/
            {completion.requiredTotal}
          </p>
          <ul className="tw:space-y-1">
            {completion.slots.map((slot) => (
              <li
                key={slot.id}
                className="tw:flex tw:items-center tw:gap-1.5 tw:text-[11px]"
              >
                {slot.complete ? (
                  <Check className="tw:h-3 tw:w-3 tw:shrink-0 tw:text-emerald-300" />
                ) : (
                  <Circle className="tw:h-3 tw:w-3 tw:shrink-0 tw:opacity-60" />
                )}
                <span className={slot.complete ? '' : 'tw:opacity-80'}>
                  {slot.label}
                </span>
              </li>
            ))}
          </ul>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default Ics204SignatureProgressRing;
